import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import type {
  AdminEventRegistration,
  EventCheckinRequest,
  EventCheckinResponse,
} from '@mds/shared/types/event-registration';
import useDebounced from '@mds/shared/hooks/use-debounced';
import Select from '@mds/shared/ui/select';

import { useSession } from '@/auth/use-session';
import CameraScanner, { cameraScanSupported } from '@/components/events/camera-scanner';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Field, { controlCls, FieldError, inputCls, selectCls } from '@/components/ui/field';
import Pager from '@/components/ui/pager';
import { eventDateLabel, eventWhen, monthKeyOf } from '@/domain/events';
import { checkinRequestOf, registrationProblem, seatsLabel } from '@/domain/registrations';
import * as events from '@/events/events.api';
import * as registrations from '@/events/registrations.api';
import { keyOf } from '@mds/shared/lib/dates';

const DOOR_PAGE = 50;

type Message = { tone: 'ok' | 'warn' | 'bad'; text: string };

const TONES: Record<Message['tone'], string> = {
  ok: 'border-brand-edge/35 bg-brand-wash text-brand-strong',
  warn: 'border-warn-edge bg-warn-wash text-warn-text',
  bad: 'border-danger-edge bg-danger-wash text-danger-text',
};

const toneOf = (outcome: EventCheckinResponse['outcome']): Message['tone'] =>
  outcome === 'valid' ? 'ok' : outcome === 'already' ? 'warn' : 'bad';

/**
 * Door check-in: pick the event, then scan a ticket, type its code, or find a
 * guest by name.
 *
 * Every check-in goes through the API, which decides — a revoked ticket, an
 * unpaid party or another night's ticket is refused there, and a second scan
 * reads as already in rather than writing twice.
 */
export default function CheckInPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayRead = can('events.read');

  // This month and next: the door is tonight, but a late check-in or an early
  // test of next week's list should not need a different screen.
  const months = useMemo(() => {
    const now = new Date();
    return [monthKeyOf(now), monthKeyOf(new Date(now.getFullYear(), now.getMonth() + 1, 1))];
  }, []);
  const programme = useQuery({
    queryKey: ['events', 'door', months],
    queryFn: async () => {
      const pages = await Promise.all(
        months.map((month) =>
          events.listEvents({ month, limit: 100, sort: 'eventDate', direction: 'asc' }),
        ),
      );
      return pages.flatMap((p) => p.data);
    },
    enabled: mayRead,
  });
  const eventList = useMemo(() => programme.data ?? [], [programme.data]);

  const [eventId, setEventId] = useState('');
  // Tonight's event is the one the door almost always wants.
  const today = keyOf(new Date());
  if (eventId === '' && eventList.length > 0) {
    const tonight = eventList.filter((e) => e.eventDate === today);
    if (tonight.length === 1) setEventId(tonight[0].id);
  }
  const event = eventList.find((e) => e.id === eventId) ?? null;

  const [camera, setCamera] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  /** What is wrong with the lookup itself, shown under the box rather than sent. */
  const [lookupError, setLookupError] = useState<string | null>(null);
  const lookupErrorId = useId();
  /** Several guests matched a typed name; the door picks one. */
  const [matches, setMatches] = useState<AdminEventRegistration[]>([]);
  const input = useRef<HTMLInputElement>(null);

  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const settled = useDebounced(filter);

  const door = useQuery({
    queryKey: ['registrations', eventId, 'door', { page, settled }],
    queryFn: () =>
      registrations.listRegistrations(eventId, {
        page,
        limit: DOOR_PAGE,
        // The API refuses a blank or overlong search rather than ignoring it.
        q: settled.trim().slice(0, 100) || undefined,
        status: 'confirmed',
        sort: 'attendeeName',
        direction: 'asc',
      }),
    enabled: eventId !== '',
  });
  const summary = door.data?.summary;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['registrations', eventId] });

  const pickEvent = (next: string) => {
    setEventId(next);
    setCamera(false);
    setCode('');
    setFilter('');
    setPage(1);
    setMatches([]);
    setMessage(null);
    setLookupError(null);
  };

  // A handheld scanner types into whatever has focus, so keep it on the box.
  useEffect(() => {
    if (eventId) input.current?.focus();
  }, [eventId]);

  const send = async (body: EventCheckinRequest) => {
    setBusy(true);
    setMatches([]);
    try {
      const result = await registrations.checkIn(eventId, body);
      setMessage({ tone: toneOf(result.outcome), text: result.message });
      if (result.outcome === 'valid') await refresh();
    } catch (e) {
      setMessage({
        tone: 'bad',
        text: registrationProblem(e, 'That did not go through. Try again.'),
      });
    } finally {
      setBusy(false);
      setCode('');
      input.current?.focus();
    }
  };

  /** A ticket code or QR payload goes straight to the API; anything else is a name to find. */
  const submit = async (raw: string) => {
    const value = raw.trim();
    if (!eventId || busy) return;
    if (!value) {
      setLookupError("Scan a ticket, or type its code or the guest's name.");
      input.current?.focus();
      return;
    }
    const request = checkinRequestOf(value);
    if (request) {
      setLookupError(null);
      return send(request);
    }
    // Anything else is a name search, which the API caps at 100 characters.
    if (value.length > 100) {
      setLookupError('Keep a name to 100 characters or fewer.');
      input.current?.focus();
      return;
    }
    setLookupError(null);

    setBusy(true);
    try {
      const found = await registrations.listRegistrations(eventId, {
        q: value,
        status: 'confirmed',
        limit: 10,
        sort: 'attendeeName',
        direction: 'asc',
      });
      if (found.data.length === 1) {
        setBusy(false);
        return send({ registrationId: found.data[0].id });
      }
      setMatches(found.data);
      setMessage(
        found.data.length === 0
          ? { tone: 'bad', text: `Nobody confirmed for this event matches "${value}".` }
          : { tone: 'warn', text: `${found.data.length} guests match "${value}". Pick one below.` },
      );
    } catch (e) {
      setMessage({ tone: 'bad', text: registrationProblem(e, 'The list could not be searched.') });
    } finally {
      setBusy(false);
    }
  };

  const undo = async (r: AdminEventRegistration) => {
    setBusy(true);
    try {
      const result = await registrations.setAttendance(eventId, r.id, false);
      setMessage({ tone: toneOf(result.outcome), text: result.message });
      await refresh();
    } catch (e) {
      setMessage({ tone: 'bad', text: registrationProblem(e, 'That did not change. Try again.') });
    } finally {
      setBusy(false);
    }
  };

  if (!mayRead)
    return (
      <EmptyState
        title="The door list needs events access"
        hint="Checking guests in reads the event's registrations, which needs the events.read permission as well. Ask an administrator to add it to your role."
      />
    );

  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_1.1fr]">
      <Card title="Scanner">
        <div className="flex flex-col gap-4">
          {/* The door picks the event first: everything below works against
              that list, so there is nothing sensible to scan without it. */}
          <Field label="Event on the door">
            <Select
              variant="field"
              className={selectCls}
              value={eventId}
              onChange={pickEvent}
              options={eventList.map((e) => ({
                value: e.id,
                label: `${eventDateLabel(e.eventDate)} · ${e.title}`,
              }))}
              placeholder={programme.isPending ? 'Loading events…' : 'Pick an event…'}
              aria-label="Event on the door"
            />
          </Field>
          {programme.isError && (
            <p role="alert" className="m-0 text-base text-danger-text">
              {programme.error instanceof ApiError
                ? programme.error.message
                : 'The events could not be loaded.'}
            </p>
          )}

          {cameraScanSupported && (
            <>
              <div className="relative flex aspect-card items-center justify-center overflow-hidden rounded-2xl bg-panel">
                <CameraScanner active={camera && !!eventId} onCode={(v) => void submit(v)} />
                <div className="absolute inset-8 rounded-xl border border-dashed border-brand/50" />
                {!camera && (
                  <span className="relative px-8 text-center font-mono text-base tracking-wide text-on-panel/85 uppercase">
                    {eventId ? 'Camera off' : 'Pick an event to begin'}
                  </span>
                )}
              </div>
              <div>
                <Button disabled={!eventId} onClick={() => setCamera((c) => !c)}>
                  {camera ? 'Stop camera' : 'Scan with camera'}
                </Button>
              </div>
            </>
          )}

          <div className="flex flex-col gap-2">
            <span className="font-mono text-sm tracking-widest text-ink-muted uppercase">
              {cameraScanSupported
                ? 'Or scan with a handheld reader, or type a code or name'
                : 'Scan with a handheld reader, or type a code or name'}
            </span>
            <div className="flex items-stretch gap-2">
              <input
                ref={input}
                className={inputCls}
                placeholder="MDS-1042 or a guest's name"
                aria-label="Ticket code, QR payload or guest name"
                disabled={!eventId}
                value={code}
                autoComplete="off"
                aria-invalid={lookupError ? true : undefined}
                aria-describedby={lookupError ? lookupErrorId : undefined}
                onChange={(e) => {
                  setCode(e.target.value);
                  setLookupError(null);
                }}
                onKeyDown={(e) => e.key === 'Enter' && void submit(code)}
              />
              <Button
                className="h-auto"
                onClick={() => void submit(code)}
                disabled={!eventId || busy}
              >
                Check in
              </Button>
            </div>
            {lookupError && <FieldError id={lookupErrorId}>{lookupError}</FieldError>}
          </div>

          {message && (
            <div
              role="status"
              className={'rounded-xl border px-4 py-3 text-base ' + TONES[message.tone]}
            >
              {message.text}
            </div>
          )}

          {matches.length > 0 && (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {matches.map((r) => (
                <DoorRow
                  key={r.id}
                  registration={r}
                  busy={busy}
                  onCheckIn={() => void send({ registrationId: r.id })}
                  onUndo={() => void undo(r)}
                />
              ))}
            </ul>
          )}
        </div>
      </Card>

      <Card
        title={event ? `${event.title} · ${eventWhen(event)}` : 'Door list'}
        action={
          summary ? (
            <Chip>
              {summary.checkedInSeats} / {summary.registeredSeats} seats in
            </Chip>
          ) : null
        }
      >
        {!eventId ? (
          <p className="m-0 text-base text-ink-muted">
            Pick the event on the door and its list appears here.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            <input
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(1);
              }}
              placeholder="Find on the list"
              aria-label="Find a guest on the door list"
              className={controlCls + ' w-full'}
            />
            {summary && summary.paymentPendingRegistrationCount > 0 && (
              <p className="m-0 text-base text-ink-muted">
                {summary.paymentPendingRegistrationCount} more{' '}
                {summary.paymentPendingRegistrationCount === 1 ? 'party has' : 'parties have'} not
                paid and cannot check in until payment is recorded under Events.
              </p>
            )}
            {door.isPending ? (
              <p className="m-0 text-base text-ink-muted">Loading the door list…</p>
            ) : door.isError ? (
              <EmptyState
                title="Could not load the door list"
                hint={
                  door.error instanceof ApiError
                    ? door.error.message
                    : 'The server could not be reached.'
                }
              />
            ) : door.data.data.length === 0 ? (
              <EmptyState
                title={settled ? 'Nobody on the list matches' : 'Nobody is confirmed yet'}
                hint={settled ? 'Try part of the name, or the ticket code.' : undefined}
              />
            ) : (
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {door.data.data.map((r) => (
                  <DoorRow
                    key={r.id}
                    registration={r}
                    busy={busy}
                    onCheckIn={() => void send({ registrationId: r.id })}
                    onUndo={() => void undo(r)}
                  />
                ))}
              </ul>
            )}
            <Pager pagination={door.data?.pagination} onPage={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}

function DoorRow({
  registration: r,
  busy,
  onCheckIn,
  onUndo,
}: {
  registration: AdminEventRegistration;
  busy: boolean;
  onCheckIn: () => void;
  onUndo: () => void;
}) {
  return (
    <li
      className={
        'flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-colors ' +
        (r.checkedIn ? 'border-brand-edge/40 bg-brand-wash' : 'border-hairline bg-surface')
      }
    >
      <div className="flex flex-col gap-0.5">
        <span className="text-base font-semibold">{r.attendeeName}</span>
        <span className="font-mono text-sm text-ink-muted">
          {r.ticket?.code ?? r.reference} · {seatsLabel(r.seatCount)}
        </span>
      </div>
      {r.checkedIn ? (
        <div className="flex items-center gap-2">
          <Chip className="border border-brand-edge/35 bg-surface text-brand-strong">
            Checked in
          </Chip>
          <Button
            variant="quiet"
            disabled={busy}
            onClick={onUndo}
            aria-label={`Undo check-in for ${r.attendeeName}`}
          >
            Undo
          </Button>
        </div>
      ) : (
        <Button
          variant="quiet"
          disabled={busy}
          onClick={onCheckIn}
          aria-label={`Check in ${r.attendeeName}`}
        >
          Check in
        </Button>
      )}
    </li>
  );
}
